import {Component, Input, OnInit} from '@angular/core';
import {PrimengTableCols} from '@sharedcommon/interfaces/primeng-table-cols';
import {PatchDto, PredictionModelDto, ReferenceServiceClient, ReferenceValueDto, VariableValueDto} from '@sharedrest/data.restservice';
import {formatDate} from '@angular/common';
import {VariableDto} from '@sharedrest/equation.restservice';
import {VariableValue} from '@admincommon/components/variable-value/variable-value.model';
import {PrimengTableVariableRequirementModel} from '@sharedcommon/model/primeng-table-variable-requirement.model';
import {PrimengTableService} from '@sharedcommon/components/primeng-table/primeng-table.service';
import {VariableValuePredictionModelValueDirective} from '@sharedcommon/directives/variable-value-prediction-model-value.directive';
import {ReferenceValueAndFormForCal} from '@admincommon/models/references/reference-calculated-values-table.model';
import {FormControl} from '@angular/forms';
import {ReferenceCalculatedValuesTableService} from '@admincommon/services/reference-calculated-values-table.service';
import {TranslateService} from '@ngx-translate/core';
import {Constants} from '@sharedcommon/util/constants.util';

export type RefCalcFormType = 'max' | 'limitable' | 'floor' | 'ceil' | 'cappable' | 'min';

@Component({
  selector: 'neq-primeng-table-cell-inputs-variable',
  templateUrl: './primeng-table-cell-inputs-variable.component.html',
  styleUrls: ['./primeng-table-cell-inputs-variable.component.scss']
})
export class PrimengTableCellInputsVariableComponent implements OnInit {

  /**
   * Column of the current cell
   */
  @Input() col: PrimengTableCols;

  /**
   * All requirement for the Variable column functionality
   */
  @Input() dynamicRequirements: PrimengTableVariableRequirementModel;

  /**
   * Line of the current cell
   */
  @Input() rowData: any;

  /**
   * Number of dynamic column present
   */
  @Input() numberDynamicColumn = 0;

  userActivity: string;
  toDisabledDueToUserRole = false;
  patternDecimal: string;

  inputTextFc = new FormControl();

  static getNativeElement(item: VariableValuePredictionModelValueDirective) {
    return item.variableValuePredictionModelValue.valueAccessor;
  }

  constructor(
    private readonly primengTableService: PrimengTableService,
    private readonly referenceService: ReferenceServiceClient,
    private readonly translate: TranslateService,
  ) { }

  ngOnInit() {
    this.patternDecimal = Constants.REGEX_DECIMAL;
  }

  /**
   * Get the tooltip error message of a number input
   * @param inputFormCtrl the FormControl connected to the input
   * @returns the tooltip error message
   */
  getTooltipErrorMessage(inputFormCtrl: FormControl) {
    const errors = inputFormCtrl.errors;
    let message = '';
    if (errors) {
      if (errors.pattern) {
        message = this.translate.instant('error.common.decimal.invalid');
      }
    }

    return message;
  }

  getAuditorTooltip(vv: VariableValueDto) {
    return vv ?
      `${
        vv.modifiedBy
      } - ${
        formatDate(vv.modificationDate, 'dd/MM/yyyy HH:mm:ss', this.dynamicRequirements.translateService.currentLang)
      }`
      : '';
  }

  getVariableValue(pm: PredictionModelDto, v: VariableDto) {
    return this.dynamicRequirements.vvCache.find(vv => vv.variable.code === v.code
      && vv.predictionModel.code === pm.code) || null;
  }

  updateVariableValue(vv: VariableValue) {
    if (vv.variableValue.value !== '') {
      const dto = new PatchDto<string>();
      dto.op = 'REPLACE';
      dto.path = '/value';
      dto.value = vv.variableValue.value;
      this.dynamicRequirements.clientService.variableValuesPatch(vv.predictionModel.id, vv.variable.code, [dto])
        .subscribe(result => {
          vv.variableValue = result;
          // special case : value in conditional variable not present in possible values
          if (vv.variable.conditionVariable && vv.variable.possibleValues.indexOf(vv.variableValue.value) === -1) {
            vv.variable.possibleValues.push(vv.variableValue.value);
          }
          this.updateVariableValueCache(vv);
        });
    } else {
      this.deleteVariableValue(vv);
    }
  }

  private updateVariableValueCache(vv: VariableValue) {
    let variableValue = this.getVariableValue(vv.predictionModel, vv.variable);
    if (!variableValue) {
      variableValue = vv;
      this.dynamicRequirements.vvCache.push(variableValue);
    }
    variableValue.variableValue = vv.variableValue;
  }

  private deleteVariableValue(vv: VariableValue) {
    this.dynamicRequirements.clientService.variableValuesDelete(vv.predictionModel.id, vv.variable.code)
      .subscribe(_result => {
        PrimengTableService.remove(this.dynamicRequirements.vvCache, x => vv.variable.code === x.variable.code
          && vv.predictionModel.code === x.predictionModel.code);
      });
  }

  /**
   * Check if all the dynamic value are initialised
   */
  hasAllDynamicValue(): boolean {
    return PrimengTableService.haveAllDynamicValue(this.dynamicRequirements);
  }

  /**********
   *
   *********/

  /**
   * Will return the value of row to send to back to update
   * @param row the row which has been trigged to be updated
   */
  getUpdatedReferenceValueFromRow(row: ReferenceValueDto): ReferenceValueDto {
    /* Shallow copy to be sure to do not update the row attributes value */
    const refValDto: ReferenceValueDto = {...row};
    /* default value for entry refValue */
    refValDto.limitable = true ;
    const isCeilAndFloorDisabled: boolean = this.disableCeilAndFloorInput(row) ;
    refValDto.ceilVal = this.getNumberInputValue(isCeilAndFloorDisabled, refValDto.ceilVal) ;
    refValDto.floorVal = this.getNumberInputValue(isCeilAndFloorDisabled, refValDto.floorVal) ;
    return refValDto ;
  }

  /**
   * Check if the disable color managment has to be set for ceiling and floor inputs
   * @param row the row where the ceiling and floor inputs are
   * @returns true if the disable color management for inputs has to be set
   */
  enableDisabledColorManagementForFloorCeilInputs(row: ReferenceValueDto): boolean {
    return this.enableDisabledColorManagementForCappableCheckBox(row) || !row.cappable ;
  }

  /**
   * Get the number from input value according to its state
   * @param isDisabled true if the input is disabled on html
   * @param inputValue the value of the input
   * @returns the corresponding value of the input according to its state
   */
  getNumberInputValue(isDisabled: boolean, inputValue: number|string): number|null {
    return (!isDisabled && (inputValue || inputValue === 0)) ? +inputValue : null ;
  }

  /**
   * Check if the input of floor or ceiling value has to be disabled
   * @param row the row where the input is
   * @returns true if the input has to be disabled
   */
  disableCeilAndFloorInput(row: ReferenceValueDto): boolean {
    return this.disableCappable(row) || !row.cappable ;
  }

  /**
   * Check if the checkbox cappable has to be disabled
   * @param row the row where the checkbox is
   * @returns true if th checkbox has to be disabled
   */
  disableCappable(row: ReferenceValueDto): boolean {
    return this.primengTableService.isDisabled || !row.cappableNutrient ;
  }


  /**
   * Return the right form from a refValForm and depending of the type value.
   *
   * @param refValForm the RefValAndForm from which we want to take
   * @param type type
   */
  returnFormFromRow(
    refValForm: ReferenceValueAndFormForCal,
    type: RefCalcFormType): FormControl {
    switch (type) {
      case 'max':
        // Get the maxForm form from a refValForm
        return ReferenceCalculatedValuesTableService.getMaxFormFromRefValAndForm(refValForm) ;
      case 'limitable':
        // Get the limitableForm form the row
        return ReferenceCalculatedValuesTableService.getLimitableFormFromRefValAndForm(refValForm) ;
      case 'floor':
        // Get the floorForm form from a refValForm
        return ReferenceCalculatedValuesTableService.getFloorFormFromRefValAndForm(refValForm) ;
      case 'ceil':
        // Get the ceilForm form from a refValForm
        return ReferenceCalculatedValuesTableService.getCeilFormFromRefValAndForm(refValForm) ;
      case 'cappable':
        // Get the cappableForm form the row
        return ReferenceCalculatedValuesTableService.getCappableFormFromRefValAndForm(refValForm) ;
      default:
        // Get the minForm form from a refValForm
        return ReferenceCalculatedValuesTableService.getMinFormFromRefValAndForm(refValForm);
    }
  }

  /**
   * Get the ReferenceValueDto with the updated value from the form
   * @param refValAndForm the object from which the ReferenceValueDto must come from
   * @param forApiRequest at true if the the reference value to from is for sending it to the API
   * @returns the corresponding ReferenceValueDto with the updated value from the form
   */
  getNewValueOfRefValDtoFromRefValAndForm(refValAndForm: ReferenceValueAndFormForCal, forApiRequest: boolean): ReferenceValueDto {
    const maxForm: FormControl = this.returnFormFromRow(refValAndForm, 'max');
    const minForm: FormControl = this.returnFormFromRow(refValAndForm, 'min');
    const ceilForm: FormControl = this.returnFormFromRow(refValAndForm, 'ceil');
    const floorForm: FormControl = this.returnFormFromRow(refValAndForm, 'floor');
    const ceilVal = this.getValFromNumberFormCtrl(ceilForm) ;
    const floorVal = this.getValFromNumberFormCtrl(floorForm) ;
    let cappableVal = this.returnFormFromRow(refValAndForm, 'cappable').value
    if (forApiRequest) {
      const validCeil = ceilVal !== null || this.returnDefaultValuePlaceholder(refValAndForm, 'ceil') !== '' ;
      const validFloor = floorVal !== null || this.returnDefaultValuePlaceholder(refValAndForm, 'floor') !== '' ;
      cappableVal = cappableVal && (validCeil || validFloor) ;
    }
    return {
      ...refValAndForm.referenceValue,
      limitable: this.returnFormFromRow(refValAndForm, 'limitable').value,
      maxVal: this.getValFromNumberFormCtrl(maxForm),
      minVal: this.getValFromNumberFormCtrl(minForm),
      cappable: cappableVal,
      ceilVal: ceilVal,
      floorVal: floorVal
    } ;
  }

  /**
   * Get the placeholder for the floor value from a row
   * @param row the row which must contain the place holder
   * @param field field identifier of ceil or floor field
   * @returns the corresponding placeholder for floor value (empty string if no placeholder to display)
   */
  returnDefaultValuePlaceholder(row: ReferenceValueAndFormForCal, field: RefCalcFormType): string | number {
    const refValue = this.getNewValueOfRefValDtoFromRefValAndForm(row, false) ;
    if (field === 'max' || field === 'min') {
      return row.referenceValue[`${field}Default`] ;
    }
    if (field === 'floor') {
      return refValue.limitableNutrient ? ReferenceServiceClient.getDefaultFloorValueFromRefVal(refValue) : '' ;
    }
    return refValue.limitableNutrient ? ReferenceServiceClient.getDefaultCeilValueFromRefVal(refValue) : '' ;
  }

  /**
   * Function called on event (change of limitable or cappable in a row)
   * @param event the event value from html
   * @param row the row from which the event is from
   * @param type field id of the column
   * @param table origin table of the column
   */
  onChangeFromHtml(event: any, row: any, type: RefCalcFormType, table = 'input-refValue-checkbox'): void {
    const checkState = event.checked ;
    if (table === 'input-refCalValue-checkbox') {
      if (type === 'limitable') {
        this.enableMinMaxFormOnLimitableChange(checkState, row) ;
      } else {
        this.enableFloorCeilFormOnCappableChange(checkState, row) ;
      }

      const firstCol: RefCalcFormType = (type === 'limitable') ? 'max' : 'floor';
      const secondCol: RefCalcFormType = (type === 'limitable') ? 'min' : 'ceil';
      if (!checkState) {
        this.returnFormFromRow(row, firstCol).setValue(null) ;
        this.returnFormFromRow(row, secondCol).setValue(null) ;
      }
      this.manageReferenceValue(row);
    } else {
      if (!checkState) {
        row.floorVal = null ;
        row.ceilVal = null ;
      }
      this.manageReferenceValueBaseRef(row);
    }
  }

  /**
   * Will send a request to send a request to update/create a referenceValue
   *
   * @param refValAndForm the RefValAndForm from what we can retrieve the Dto to send
   */
  manageReferenceValue(refValAndForm: ReferenceValueAndFormForCal) {
    if (refValAndForm.form.invalid) {
      /* Do not send any request if the form is not valid */
      return;
    }
    const refValDto: ReferenceValueDto = this.getNewValueOfRefValDtoFromRefValAndForm(refValAndForm, true) ;
    this.referenceService.referenceValuesPut(this.primengTableService.referenceId, refValDto).subscribe();
  }

  /**
   * Will send a request to send a request to update/create a referenceValue
   *
   * @param row the RefValAndForm from what we can retrieve the Dto to send
   * @param isValid type of the origin table
   */
  manageReferenceValueBaseRef(row: ReferenceValueDto) {
    const refValDtoToSend: ReferenceValueDto = this.getUpdatedReferenceValueFromRow(row) ;
    this.referenceService.referenceValuesPut(this.primengTableService.referenceId, refValDtoToSend).subscribe();
  }

  /**
   * Excecuted on limtableForm changes. It will enable/disable on consequences
   * @param limitableValue the value that has limitableForm
   * @param refValAndForm the object from what the limitableForm is from
   */
  enableMinMaxFormOnLimitableChange(limitableValue: boolean, refValAndForm: ReferenceValueAndFormForCal): void {
    const maxForm: FormControl = this.returnFormFromRow(refValAndForm, 'max') ;
    const minForm: FormControl = this.returnFormFromRow(refValAndForm, 'min') ;
    if (limitableValue) {
      maxForm.enable({ emitEvent: false }) ;
      minForm.enable({ emitEvent: false }) ;
    } else {
      maxForm.disable({ emitEvent: false }) ;
      minForm.disable({ emitEvent: false }) ;
    }
  }

  /**
   * Excecuted on cappableForm changes. It will enable/disable and reset value on consequences
   * @param cappableValue the value that has cappableForm
   * @param refValAndForm the object from what the limitableForm is from
   */
  enableFloorCeilFormOnCappableChange(cappableValue: boolean, refValAndForm: ReferenceValueAndFormForCal): void {
    const floorForm: FormControl = this.returnFormFromRow(refValAndForm, 'floor') ;
    const ceilForm: FormControl = this.returnFormFromRow(refValAndForm, 'ceil') ;
    if (cappableValue) {
      floorForm.enable({ emitEvent: false }) ;
      ceilForm.enable({ emitEvent: false }) ;
    } else {
      floorForm.disable({ emitEvent: false }) ;
      floorForm.setValue(null, { emitEvent: false }) ;
      ceilForm.disable({ emitEvent: false }) ;
      ceilForm.setValue(null, { emitEvent: false }) ;
    }
  }

  /**
   * Check if the disable color managment has to be set for input of type
   *
   * @param row
   * @param type
   * @returns true if the disable color management for inputs has to be set
   */
  enableDisabledColorManagement(row: ReferenceValueAndFormForCal, type: RefCalcFormType): boolean {
    if (type === 'min' || type === 'max') {
      return !row.referenceValue.limitableNutrient || !this.returnFormFromRow(row, 'limitable').value ;
    }

    if (type === 'floor' || type === 'ceil') {
      return !row.referenceValue.cappableNutrient || !this.returnFormFromRow(row, 'cappable').value ;
    }

    return !row.referenceValue[`${type}Nutrient`] ;
  }

  /**
   * Return the default value of the given field type
   * @param row the row from which we want the floor value
   * @param type Type of the cell for ref and refCalc value
   * @returns the floor value of the row
   */
  returnDefault(row: ReferenceValueDto, type: string): number {
    if (type === 'floor') {
      return ReferenceServiceClient.getDefaultFloorValueFromRefVal(row);
    }
    if (type === 'ceil') {
      return ReferenceServiceClient.getDefaultCeilValueFromRefVal(row);
    }
    return 0;
  }

  /**
   * Return the right tooltip for the form of type RefCalcFormType.
   *
   * @param row the row from which we want to take
   * @param type type of the form
   *
   * @return string
   */
  returnTooltipForForm(row: ReferenceValueAndFormForCal, type: RefCalcFormType): string {
    return this.getTooltipErrorMessageFromNumberInputForm(this.returnFormFromRow(row, type));
  }

  /**
   * Get the tooltip to explain the detected errors for a FormControl which excpects a number
   * @param inputFormControl the formControl which expects a number and is connected to an input
   * @returns the tooltip message in case of error
   */
  getTooltipErrorMessageFromNumberInputForm(inputFormControl: FormControl) {
    const errors = inputFormControl.errors;
    let message = '';
    if (errors) {
      if (errors.pattern) {
        message = this.translate.instant('error.common.decimal.invalid');
      }
    }
    return message;
  }

  /**
   * Check if the disable color managment has to be set for ceiling and floor inputs
   * @param row the row where the ceiling and floor inputs are
   * @returns true if the disable color management for inputs has to be set
   */
  enableDisabledColorManagementRefValue(row: ReferenceValueDto): boolean {
    return this.enableDisabledColorManagementForCappableCheckBox(row) || !row.cappable ;
  }

  /**
   * Check if the disable color managment has to be set for cappable checkbox
   * @param row the row where cappable checkbox is
   * @returns true if the disable color management for the checkbox has to be set
   */
  enableDisabledColorManagementForCappableCheckBox(row: ReferenceValueDto): boolean {
    return !row.cappableNutrient;
  }


  /**
   * Get the corresponding value for a FormControl which must expect a number
   * @param numberFormCtrl the form control which expect a number
   * @returns the corresponding value
   */
  private getValFromNumberFormCtrl(numberFormCtrl: FormControl): any {
    const formValue = numberFormCtrl.value ;
    return (numberFormCtrl.enabled && (formValue || formValue === 0)) ? formValue : null ;
  }
}
